import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PaneResizeHandle } from '@/components/PaneResizeHandle';
import { usePref } from '@/hooks/use-pref';
import { usePhoneFullScreenStyle } from '@/hooks/use-visual-viewport';
import { UI_PREFS } from '@/lib/ui-prefs';
import { cn } from '@/lib/utils';
import { useChatPanel, useChatPanelLayout } from './chat-panel-state';

/** The narrowest the docked panel goes, and the least it leaves the page beside it. */
const MIN_WIDTH = 320;
const PAGE_MIN_WIDTH = 448;

/**
 * The chat panel's frame (T-78), loaded with the app so it shows at once, with a loading state while the
 * chat inside it loads (ChatDock). Docked beside the page from 1024px (drag its left edge, or focus it and
 * press ← or →, to resize it); over the right of the page below that; over the whole screen on a phone,
 * where it's a dialog and the page behind is inert (AppLayout). Over the page, Escape closes it.
 */
export function ChatDockFrame({ children }: { children: ReactNode }) {
  const layout = useChatPanelLayout();
  const { setOpen } = useChatPanel();
  const phoneStyle = usePhoneFullScreenStyle(layout === 'cover');
  // Saved per browser when a drag ends, followed live while dragging.
  const [savedWidth, saveWidth] = usePref(UI_PREFS.chatPanelWidth);
  const [draggedWidth, setDraggedWidth] = useState<number | null>(null);
  const width = draggedWidth ?? savedWidth;
  const docked = layout === 'dock';
  const ref = useRef<HTMLElement | null>(null);
  // The docked panel's width, as --chat-dock-width on the root (pop-ups are portalled to the body), so a
  // pop-up over the page centres in the part beside it (TaskPopup). Its size is an outside system
  // (layout), so this is an effect.
  useEffect(() => {
    const el = ref.current;
    const shell = document.documentElement;
    if (!el || !docked || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => shell.style.setProperty('--chat-dock-width', `${el.getBoundingClientRect().width}px`));
    observer.observe(el);
    return () => {
      observer.disconnect();
      shell.style.removeProperty('--chat-dock-width');
    };
  }, [docked]);

  return (
    <aside
      ref={ref}
      data-chat-panel
      role={layout === 'cover' ? 'dialog' : 'complementary'}
      aria-modal={layout === 'cover' || undefined}
      aria-label="Chat panel"
      onKeyDown={(event) => {
        // Menus and dialogs inside handle their own Escape first (they mark it handled).
        if (docked || event.key !== 'Escape' || event.defaultPrevented) return;
        event.preventDefault();
        setOpen(false);
      }}
      className={cn(
        'flex flex-col bg-background',
        docked
          ? 'sticky top-[var(--app-topbar-height)] h-[calc(100dvh-var(--app-topbar-height))] shrink-0 self-start border-l'
          : 'fixed top-0 right-0 bottom-0 z-40 w-full border-l shadow-2xl sm:w-[28rem]'
      )}
      style={docked ? { width, minWidth: MIN_WIDTH, maxWidth: `max(${MIN_WIDTH}px, calc(100% - ${PAGE_MIN_WIDTH}px))` } : phoneStyle}
    >
      {docked ? (
        <PaneResizeHandle
          width={width}
          min={MIN_WIDTH}
          max={(handle) => (handle.closest<HTMLElement>('[data-slot="sidebar-inset"]')?.clientWidth ?? window.innerWidth) - PAGE_MIN_WIDTH}
          grows="left"
          onResize={setDraggedWidth}
          onResizeEnd={(next) => {
            saveWidth(next);
            setDraggedWidth(null);
          }}
          label="Resize the chat panel"
          className="absolute inset-y-0 -left-2 z-10"
        />
      ) : null}
      {children}
    </aside>
  );
}
